/**
 * DevExtreme HtmlEditor (Quill) stores blocks as <p>…</p> and often keeps a trailing
 * <br> inside each paragraph. A naive "</p> → <br>" conversion then doubles spacing
 * on every open/save cycle. Convert paragraphs to single <br> separators without
 * stacking extras, while leaving tables/lists/etc. intact.
 */
export function newsletterHtmlToBr(html: string | null | undefined): string {
  if (!html?.trim()) return "";

  const container = document.createElement("div");
  container.innerHTML = html;

  const stripEdgeBreaks = (inner: string) =>
    inner
      .replace(/^(?:\s|<br\s*\/?>|&nbsp;|\u00a0)+/gi, "")
      .replace(/(?:\s|<br\s*\/?>|&nbsp;|\u00a0)+$/gi, "")
      .trim();

  const isEffectivelyEmpty = (inner: string) =>
    !inner || /^(?:<br\s*\/?>|&nbsp;|\u00a0|\s)*$/i.test(inner);

  const collapseBreaks = (value: string) =>
    value
      .replace(/(?:<br\s*\/?>\s*){3,}/gi, "<br><br>")
      .replace(/(?:<br\s*\/?>\s*)+$/gi, "");

  const children = Array.from(container.childNodes);
  if (children.length === 0) {
    return collapseBreaks(stripEdgeBreaks(html));
  }

  const hasParagraphBlocks = children.some(
    (node) =>
      node.nodeType === Node.ELEMENT_NODE &&
      ["P", "DIV"].includes((node as HTMLElement).tagName)
  );

  // Already BR-based markup — only collapse runaway spacing
  if (!hasParagraphBlocks) {
    return collapseBreaks(html.trim());
  }

  const parts: string[] = [];

  for (const node of children) {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent ?? "").trim();
      if (text) parts.push(text);
      continue;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) continue;

    const el = node as HTMLElement;
    const tag = el.tagName.toLowerCase();

    if (tag === "br") {
      if (parts.length > 0 && parts[parts.length - 1] !== "") {
        parts.push("");
      }
      continue;
    }

    if (tag === "p" || tag === "div") {
      // Keep structural wrappers (tables, nested blocks) as-is
      if (tag === "div" && el.querySelector("table, p, div, ul, ol")) {
        parts.push(el.outerHTML);
        continue;
      }

      const inner = stripEdgeBreaks(el.innerHTML);
      if (isEffectivelyEmpty(inner)) {
        if (parts.length > 0 && parts[parts.length - 1] !== "") {
          parts.push("");
        }
        continue;
      }
      parts.push(inner);
      continue;
    }

    parts.push(el.outerHTML);
  }

  while (parts.length > 0 && parts[parts.length - 1] === "") {
    parts.pop();
  }

  return collapseBreaks(parts.join("<br>"));
}
